import GuideLayout, {
  H2,
  H3,
  P,
  UL,
  OL,
  Callout,
  ExternalLink,
  Strong,
  Em,
} from "../ui/GuideLayout.jsx";

export default function RenovacaoVisto() {
  return (
    <GuideLayout
      title="Como renovar seu visto e zairyu card no Japão"
      subtitle="Renovação do zairyu card, mudança de status, e o que muda com o novo Tokutei Zairyu Card a partir de junho de 2026."
      slug="renovacao-visto-zairyu-card"
      description="Guia completo de renovação do zairyu card no Japão em 2026. Documentos, prazos, novo Tokutei Zairyu Card (junho 2026), erros comuns."
      updated="Maio de 2026"
      readTime="10 min"
    >
      <P>
        O zairyu card (在留カード) é o documento mais importante de um
        estrangeiro no Japão. Sem ele em dia, você não consegue trabalhar,
        abrir conta no banco, alugar apartamento, nem entrar e sair do país.
        E ele <Strong>vence</Strong> — geralmente a cada 1, 3 ou 5 anos
        dependendo do seu status; permanentes a cada 7 anos.
      </P>
      <P>
        Em 2026 tem uma mudança grande: a partir de <Strong>14 de junho de
        2026</Strong>, o Japão começa a emitir o novo <Em>Tokutei Zairyu
        Card</Em> (特定在留カード), que <Strong>combina zairyu card +
        My Number card</Strong> em um único documento. Vamos cobrir os dois
        cenários: renovação antiga e migração para o novo cartão.
      </P>

      <H2 id="diferenca-importante">Diferença importante: status vs cartão</H2>
      <P>
        Existem dois conceitos diferentes que as pessoas confundem:
      </P>
      <UL>
        <li>
          <Strong>Status de residência (在留資格 / zairyu shikaku):</Strong>{" "}
          o tipo do seu visto — &ldquo;definite activities&rdquo;, &ldquo;engineer&rdquo;,
          &ldquo;spouse&rdquo;, &ldquo;permanent resident&rdquo;, &ldquo;long-term resident&rdquo;.
          Esse é o seu direito legal de morar no Japão.
        </li>
        <li>
          <Strong>Zairyu card (在留カード):</Strong> o cartão físico que prova
          esse status. Tem validade limitada e precisa ser renovado.
        </li>
      </UL>
      <Callout>
        Para <Em>permanentes</Em>: seu <Strong>status</Strong> é eterno —
        não vence nunca. Mas o <Strong>cartão</Strong> sim, a cada 7 anos.
        Não confunda &ldquo;renovar o cartão&rdquo; com &ldquo;renovar o
        visto&rdquo; — são processos diferentes.
      </Callout>

      <H2 id="quando-renovar">Quando começar a renovação</H2>

      <H3>Status temporário (engineer, definite activities, long-term, etc.)</H3>
      <P>
        Você renova o status (extensão de período de estadia) junto com o cartão.
        Pode aplicar <Strong>até 3 meses antes</Strong> da data de validade. Aplique cedo —
        a imigração às vezes leva 1-2 meses para processar.
      </P>

      <H3>Permanente (永住者)</H3>
      <P>
        Só renova o <Strong>cartão</Strong>, não o status. Pode aplicar até{" "}
        <Strong>2 meses antes</Strong> da validade. Geralmente sai no mesmo
        dia ou poucos dias depois.
      </P>

      <Callout kind="warn">
        <Strong>Atrasar é grave.</Strong> Se você deixa o zairyu vencer:
        <UL>
          <li>Multa de até ¥200.000</li>
          <li>Pode perder o status de residência</li>
          <li>Pode ser deportado em casos extremos</li>
        </UL>
        Marque no calendário 4 meses antes da validade.
      </Callout>

      <H2 id="documentos-comuns">Documentos comuns (todos os status)</H2>
      <OL>
        <li><Strong>Passaporte</Strong> em vigor</li>
        <li><Strong>Zairyu card atual</Strong></li>
        <li>
          <Strong>Formulário de aplicação</Strong> preenchido (申請書 /
          shinseisho) — disponível no site da{" "}
          <ExternalLink href="https://www.moj.go.jp/isa/applications/procedures/">
            Immigration Services Agency (ISA)
          </ExternalLink>
        </li>
        <li>
          <Strong>Foto recente 4x3 cm</Strong> (fundo branco, sem sorriso,
          tirada nos últimos 3 meses)
        </li>
        <li>
          <Strong>Taxa de processamento</Strong>: ¥4.000 em revenue stamp
          (収入印紙 / shunyu inshi) para temporário, ¥6.000 para permanente.
          Compra no balcão do koban ou na própria imigração.
        </li>
        <li>
          <Strong>Inkan ou assinatura</Strong>
        </li>
      </OL>

      <H2 id="documentos-trabalhador">Documentos adicionais para quem tem visto de trabalho</H2>
      <UL>
        <li>
          <Strong>Certificado de trabalho (在職証明書 / zaishoku shomeisho):</Strong>{" "}
          peça no RH da sua empresa. Comprova que você ainda trabalha lá.
        </li>
        <li>
          <Strong>Comprovante de renda (源泉徴収票 / gensen choshuhyo):</Strong>{" "}
          do ano fiscal anterior. A empresa fornece em janeiro.
        </li>
        <li>
          <Strong>Comprovante de imposto residencial (住民税の納税証明書):</Strong>{" "}
          peça na sua prefeitura. Mostra que você pagou imposto residencial.
        </li>
        <li>
          <Strong>Comprovante de contribuição previdenciária (年金記録):</Strong>{" "}
          se shakai hoken, pode pedir nenkin office; se kokumin, prefeitura.
        </li>
      </UL>

      <H2 id="documentos-permanente">Documentos para permanente (apenas cartão)</H2>
      <P>
        Renovação só do cartão para permanente é simples:
      </P>
      <UL>
        <li>Passaporte + zairyu card</li>
        <li>Formulário (separado do de extensão)</li>
        <li>Foto</li>
        <li>Taxa de ¥6.000 em revenue stamp</li>
      </UL>
      <P>
        Não precisa de comprovante de trabalho, renda ou imposto. Mas atenção:
        se você ficar fora do Japão por mais de 1 ano sem permissão de reentrada,
        perde o status de permanente automaticamente.
      </P>

      <H2 id="onde-ir">Onde aplicar</H2>
      <P>
        Você só pode aplicar no <Strong>escritório da imigração com
        jurisdição sobre o seu endereço registrado</Strong>. Não é onde você
        prefere — é definido pelo seu juuminhyou (住民票).
      </P>
      <P>
        Principais escritórios:
      </P>
      <UL>
        <li>
          <ExternalLink href="https://www.moj.go.jp/isa/about/region/tokyo/index.html">
            Tóquio Immigration
          </ExternalLink>{" "}
          — Shinagawa
        </li>
        <li>
          <ExternalLink href="https://www.moj.go.jp/isa/about/region/osaka/index.html">
            Osaka Immigration
          </ExternalLink>{" "}
          — atende Kansai inteiro
        </li>
        <li>
          <ExternalLink href="https://www.moj.go.jp/isa/about/region/nagoya/index.html">
            Nagoya Immigration
          </ExternalLink>{" "}
          — atende Aichi, Mie, Gifu, Shizuoka (parte)
        </li>
        <li>
          <ExternalLink href="https://www.moj.go.jp/isa/about/region/tokyo/index.html">
            Branch offices regionais
          </ExternalLink>{" "}
          — Hamamatsu, Toyohashi, Maizuru, Sakai, etc.
        </li>
      </UL>

      <H2 id="processo">O processo dia-a-dia</H2>
      <OL>
        <li>
          <Strong>3 meses antes:</Strong> junte documentos. Tire foto. Peça
          certificados na empresa e prefeitura.
        </li>
        <li>
          <Strong>Vá à imigração com tudo:</Strong> abra cedo. Imigração lota
          de pessoas, espera de 2-5 horas é normal em alta temporada (março,
          abril, setembro).
        </li>
        <li>
          <Strong>Submissão:</Strong> entrega documentos, paga taxa, recebe
          comprovante de aplicação.
        </li>
        <li>
          <Strong>Espera 2-6 semanas</Strong>: cartão antigo continua válido
          enquanto aguarda. Você recebe postal pra buscar quando estiver pronto.
        </li>
        <li>
          <Strong>Retira o novo cartão</Strong> presencialmente. Leva
          passaporte, postal e cartão antigo.
        </li>
      </OL>

      <H2 id="online-renewal">Renovação online (smartphone)</H2>
      <P>
        Desde 2023, a imigração permite aplicação online para algumas
        categorias. Você precisa de:
      </P>
      <UL>
        <li>My Number card com chip ativo</li>
        <li>Smartphone com NFC e o app oficial &ldquo;ISA Online&rdquo;</li>
        <li>Conta no portal da ISA</li>
      </UL>
      <P>
        Você submete tudo digitalmente, mas ainda precisa <Strong>ir à
        imigração para retirar o cartão físico</Strong>. Economiza só a fila
        da aplicação, não a da retirada.
      </P>

      <H2 id="tokutei-zairyu">Novo Tokutei Zairyu Card (junho 2026)</H2>
      <P>
        A partir de <Strong>14 de junho de 2026</Strong>, o Japão começa a
        emitir o novo cartão integrado: <Em>Tokutei Zairyu Card</Em> (特定在留カード).
        Ele <Strong>combina</Strong> seu zairyu card + My Number card em um
        único cartão.
      </P>
      <P>
        Vantagens anunciadas:
      </P>
      <UL>
        <li>Um único cartão pra carregar</li>
        <li>
          Mudança de endereço atualiza automaticamente no sistema da imigração
          E no My Number (antes eram dois processos separados)
        </li>
        <li>Renovação de visto atualiza My Number na hora</li>
        <li>Menos burocracia em cartórios e prefeituras</li>
      </UL>
      <P>
        O que você precisa saber:
      </P>
      <UL>
        <li>
          <Strong>Novos residentes</Strong> a partir de 14/06/2026: recebem o
          Tokutei Zairyu automaticamente
        </li>
        <li>
          <Strong>Quem já tem zairyu + My Number:</Strong> mantém os dois até
          a próxima renovação. Não precisa correr para trocar agora.
        </li>
        <li>
          <Strong>Renovações depois de 14/06:</Strong> automaticamente migra
          pro cartão único
        </li>
        <li>
          <Strong>Quem não tem My Number ainda:</Strong> não é obrigatório,
          mas vai facilitar muito ter — pegue na prefeitura
        </li>
      </UL>
      <Callout>
        Esse é uma das maiores mudanças administrativas para estrangeiros no
        Japão em anos. Acompanhe os anúncios da{" "}
        <ExternalLink href="https://www.moj.go.jp/isa/">
          Immigration Services Agency
        </ExternalLink>
        {" "}para detalhes específicos do seu caso.
      </Callout>

      <H2 id="mudanca-status">Mudar de status (não só renovar)</H2>
      <P>
        Diferente de simples renovação. Você muda de status quando:
      </P>
      <UL>
        <li>Casa com japonês → muda pra spouse visa</li>
        <li>Termina o visto de estudante → muda pra trabalho</li>
        <li>Atinge requisitos pra permanente → aplica pra eijuken</li>
        <li>Empresa muda categoria do trabalho → outro tipo de work visa</li>
      </UL>
      <P>
        Processo:
      </P>
      <UL>
        <li>Documentação muito mais pesada (5x mais documentos que renovação simples)</li>
        <li>Processo demora 2-6 meses geralmente</li>
        <li>Pode ser rejeitado — não é automático</li>
        <li>Custa ¥4.000 mesmo em mudança</li>
      </UL>
      <P>
        Para mudança complexa, considere contratar um{" "}
        <Em>gyouseishoshi</Em> (行政書士 — escrivão administrativo). Custo
        típico: ¥80.000–200.000 dependendo do caso. Vale a pena pra
        aplicação de permanente ou casos onde rejeição custaria caro.
      </P>

      <H2 id="erros-comuns">Erros comuns que causam atraso</H2>
      <UL>
        <li>
          <Strong>Não pagar imposto residencial:</Strong> imigração consulta
          esse dado. Atraso ou inadimplência pode causar rejeição. Quite todos
          os impostos antes de aplicar.
        </li>
        <li>
          <Strong>Não pagar shakai/kokumin nenkin:</Strong> mesmo problema.
          A partir de 2024, é checagem padrão.
        </li>
        <li>
          <Strong>Foto fora do padrão:</Strong> sorriso, fundo colorido,
          óculos com reflexo são causa de rejeição na hora.
        </li>
        <li>
          <Strong>Mudou de endereço e não atualizou juuminhyou:</Strong> tem
          14 dias após mudar pra atualizar. Imigração pode ver discrepância.
        </li>
        <li>
          <Strong>Não declarar trabalho extra:</Strong> se você tem segundo
          emprego e não está formalizado, imigração pode ver via dados
          fiscais.
        </li>
      </UL>

      <H2 id="conclusao">Resumindo</H2>
      <OL>
        <li>Marque no calendário <Strong>4 meses antes</Strong> da validade</li>
        <li>Junte documentos: passaporte, zairyu, foto, formulário, taxa</li>
        <li>Trabalhador: junte certificado + comprovante de renda + impostos quitados</li>
        <li>Vá à imigração da sua jurisdição cedo</li>
        <li>Aguarde 2-6 semanas, retire o cartão novo</li>
        <li>A partir de junho/2026: novo Tokutei Zairyu vem automático nas renovações</li>
      </OL>
      <P>
        Para detalhes específicos do seu caso (que mudam frequentemente),
        consulte o site oficial:{" "}
        <ExternalLink href="https://www.moj.go.jp/isa/">
          moj.go.jp/isa
        </ExternalLink>
        . Para casos complexos, gyouseishoshi compensa o investimento.
      </P>
    </GuideLayout>
  );
}
